import { Stack, Text, useTheme } from '@ltvco/refresh-lib/theme';
import { AutoAwesomeOutlined } from '@mui/icons-material';
import React from 'react';

import { AnalyzingStatus } from '../AnalyzingStatus';
import { useVehicleMatchmakerSearch } from '../hooks/useVehicleMatchmakerSearch';

import { PreferencesForm } from './PreferencesForm';

interface IEnterPreferencesInstructionProps {
  onSendMessage?: (message?: string) => void;
  hasConversationStarted: boolean;
  shouldResetInputs: boolean;
  onResetComplete: () => void;
  onProcessingStateChange?: (isProcessing: boolean) => void;
  isLoading: boolean;
}

/**
 * EnterPreferencesInstruction - Shows instruction to enter vehicle preferences
 * The input fields are only visible when conversation hasn't started yet
 * Sends preferences data to the VehicleMatchmaker bot
 * @component
 */
export const EnterPreferencesInstruction: React.FC<
  IEnterPreferencesInstructionProps
> = ({
  hasConversationStarted,
  onSendMessage,
  shouldResetInputs,
  onResetComplete,
  onProcessingStateChange,
  isLoading,
}) => {
  const {
    budgetMin,
    budgetMax,
    vehicleType,
    usage,
    description,
    error,
    hasSubmitted,
    handleBudgetMinChange,
    handleBudgetMaxChange,
    handleVehicleTypeChange,
    handleUsageChange,
    handleDescriptionChange,
    handleSubmit,
  } = useVehicleMatchmakerSearch({
    onSendMessage,
    shouldResetInputs,
    onResetComplete,
    onProcessingStateChange,
  });
  const theme = useTheme();

  // Show form when nothing has been submitted and conversation hasn't started
  const showForm = !isLoading && !hasSubmitted && !hasConversationStarted;
  const showAnalyzingStatus =
    (hasSubmitted || hasConversationStarted) && !isLoading && !showForm;

  return (
    <Stack width="100%" justifyContent="center" alignItems="center">
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        p={2}
        mb={2}
        sx={{ backgroundColor: 'background.default', borderRadius: 2 }}
      >
        <AutoAwesomeOutlined sx={{ color: theme.palette.primary.main }} />
        <Text variant="body1">
          Hi! I&apos;m your AI vehicle research assistant. Let&apos;s start with
          a few questions to find the right match.
        </Text>
      </Stack>
      <Stack width="100%" maxWidth={800}>
        {showForm && (
          <Stack
            direction="column"
            spacing={1}
            width={{ xs: '100%', md: 'fit-content' }}
            maxWidth="760px"
            p={2}
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <Stack spacing={0.5} alignItems="flex-start" direction="row">
              <AutoAwesomeOutlined sx={{ color: theme.palette.primary.main }} />
              <Stack alignItems="flex-start" gap={1}>
                <Text variant="h6" mb={0} ml={1}>
                  Budget Range
                </Text>
                <PreferencesForm
                  budgetMin={budgetMin}
                  budgetMax={budgetMax}
                  vehicleType={vehicleType}
                  usage={usage}
                  description={description}
                  error={error}
                  isLoading={isLoading}
                  onBudgetMinChange={handleBudgetMinChange}
                  onBudgetMaxChange={handleBudgetMaxChange}
                  onVehicleTypeChange={handleVehicleTypeChange}
                  onUsageChange={handleUsageChange}
                  onDescriptionChange={handleDescriptionChange}
                  onSubmit={handleSubmit}
                />
              </Stack>
            </Stack>
          </Stack>
        )}
        {showAnalyzingStatus && (
          <AnalyzingStatus
            budgetMin={budgetMin}
            budgetMax={budgetMax}
            vehicleType={vehicleType}
            usage={usage}
          />
        )}
      </Stack>
    </Stack>
  );
};
